import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useSizingImpact from "@babackapp/private-api-client/client/use-sizing-impact";
import useTranslation from "@babackapp/translations/use-translation";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { subMonths } from "date-fns";
import { useMemo } from "react";
import { Link } from "react-router-dom";

export function SizingUpsellCallout() {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const { from, to } = useMemo(() => {
    const to = Date.now();
    return { from: subMonths(to, 3).getTime(), to };
  }, []);

  const { data: impact } = useSizingImpact({
    shopId: shop.id,
    fromTimestamp: from,
    toTimestamp: to,
  });

  const totalReturns = useMemo(() => {
    if (!impact?.timeSeries?.length) return null;
    const sum = impact.timeSeries.reduce(
      (acc, item) => acc + item.totalReturns,
      0,
    );
    return sum > 0 ? sum.toLocaleString() : null;
  }, [impact]);

  const sizingReturnRate =
    impact?.beforeRate != null && impact.beforeRate > 0
      ? `${(impact.beforeRate * 100).toFixed(0)} %`
      : null;

  return (
    <div className="col-span-4 rounded-xl border border-violet-200 bg-violet-50 p-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-violet-900">
              {t("sizing.upsell.title")}
            </h3>
            <p className="mt-1.5 text-sm text-violet-700 max-w-lg">
              {t("sizing.upsell.description")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {totalReturns !== null && (
              <StatPill
                value={totalReturns}
                label={t("sizing.upsell.totalReturns")}
              />
            )}
            {sizingReturnRate !== null && (
              <StatPill
                value={sizingReturnRate}
                label={t("sizing.upsell.sizingReturnRate")}
              />
            )}
            <StatPill
              value={t("sizing.upsell.installTimeValue")}
              label={t("sizing.upsell.installTimeLabel")}
            />
          </div>
        </div>
        <Link
          to={`/shops/${shop.id}/settings/billing`}
          className="flex shrink-0 items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
        >
          <LockClosedIcon className="h-3.5 w-3.5" />
          {t("sizing.upsell.cta")}
        </Link>
      </div>
    </div>
  );
}

function StatPill({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-sm shadow-sm ring-1 ring-violet-100">
      <span className="font-bold text-violet-900">{value}</span>
      <span className="text-violet-600">{label}</span>
    </div>
  );
}
